import { AudioConfig, StsConfig } from './deepgramUtils';

const audioConfig: AudioConfig = {
  input: {
    encoding: 'linear16',
    sample_rate: 48000
  },
  output: {
    encoding: 'linear16',
    sample_rate: 48000,
    container: 'none'
  }
};

export const baseConfig = {
  type: 'SettingsConfiguration',
  audio: audioConfig,
  agent: {
    listen: { model: 'nova-3', keyterms: ['GFH', 'CIF', 'ROI', 'COC', 'BHD', 'Wakala', 'ESG', 'CMD'] /*, endpointing: 100*/ },
    speak: { model: 'aura-asteria-en' },
    think: {
      provider: {
        type: 'custom',
      },
      functions: [
        {
          name: 'ask_question',
          description: 'Processes user queries and returns a response',
          parameters: {
            type: 'object',
            properties: {
              question: {
                type: 'string',
                description: "User's question"
              }
            },
            required: ['question']
          }
        }
      ],
      model: 'gpt-4o' // Keep it if relevant; else remove
    }
  }
};

export const stsConfig: StsConfig = {
  ...baseConfig,
  agent: {
    ...baseConfig.agent,
    think: {
      ...baseConfig.agent.think,
      provider: { type: 'open_ai', fallback_to_groq: false },
      instructions: `
                  ## Base instructions
                  You are a helpful voice assistant powered by Deepgram.
                  ALWAYS call the provided function instead of generating text responses.
                  If the user's input is a greeting, farewell, or small talk (e.g., "hello", "thank you", "goodbye", "how are you"), then also return a function call with the appropriate type (e.g., "greeting", "thanks", "farewell", etc.).
                  If the user says phrases like "Medical Clinics Two", "Student Housing Three", or "US Logistics Two", convert the spelled-out numbers like "Two", "Three" into their Roman numeral equivalents such as "II", "III" respectively. Keep the rest of the phrase exactly as spoken. These are complete proper names and should be written with no punctuation between the words and roman numerals. Correct examples include "Medical Clinics II", "Student Housing III", and "US Logistics II".
                  If the function call is invalid, return an error message instead of answering.
                  Do not modify or rephrase the user's question. Pass it to the function exactly as received.
                  Your job is to take the user's input and map it to the correct function parameters.
                  Ensure you call the function with proper arguments based on the user's query.
                  `,
      functions: [
        {
          name: 'ask_question',
          description: `You are generating responses that will be spoken using a Text-to-Speech (TTS) system.
                        The input and output text may contain intentionally modified spellings or formatting (e.g., "You s dee" for USD or "G F H" for GFH) designed to guide pronunciation.
                        Do not correct, rephrase, or alter these spellings or formats.
                        Your response should preserve all such phonetic or stylistic choices exactly as they appear, as they are essential for correct TTS pronunciation.
                        Any word in capital letters and less than 4 charactersm read it with space in between each characters. (e.g., "USD" will be "U S D")
                        Please pronounce Roman numerals like I, II, III, IV, etc., in English as 'one', 'two', 'three', 'four' respectively.
                        Assume the entire response is being read aloud — clarity, pacing, and pronunciation matter.`,
          parameters: {
            type: 'object',
            properties: {
              question: {
                type: 'string',
                description: "User's question"
              }
            },
            required: ['question']
          }
        }
      ]
    }
  },context: {
    messages: [
      {
        content: 'Hello, how can I help you?',
        role: 'assistant',
      },
    ],
    replay: true,
  },
};

export const deepgramWordPronounceMapping: { [key: string]: string } = {
  GFH: 'G F H',
  USD: 'U S D',
  CIF: 'C I F',
  ROI: 'R O I',
  COC: 'C O C',
  BHD: 'B H D',
};

export const numberWordToRomanMap: { [key: string]: string } = {
  one: 'I',
  two: 'II',
  three: 'III',
  four: 'IV',
  five: 'V',
  six: 'VI',
  seven: 'VII',
  eight: 'VIII',
  nine: 'IX',
  ten: 'X',
};

export const timeUnits = ['year', 'years', 'month', 'months', 'day', 'days'];

export interface ChatPayloadMessage {
  isVoiceAgent?: boolean;
  sender: 'user' | 'bot';
  message: string;
}
